// src/app/store/index.ts
// ─────────────────────────────────────────────────────────────────────────────
// All RTK Query feature APIs are now `apiBase.injectEndpoints(...)` calls on one
// shared `apiBase` instance (see src/shared/services/apiBase.ts) instead of each
// feature owning its own createApi()/reducerPath/middleware. That means this
// store only ever needs to register ONE API reducer + ONE API middleware, no
// matter how many feature API files exist — individual feature API modules are
// no longer imported here at all. They get pulled into the bundle (and inject
// their endpoints into the shared cache) only when something that actually
// needs them — a hook, a lazy-loaded page — imports them directly.
//
// To add a new feature API: just injectEndpoints() on `apiBase` in the feature's
// own services file. Nothing in this store file needs to change.
// ─────────────────────────────────────────────────────────────────────────────

import { configureStore, combineReducers } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import type { TypedUseSelectorHook } from "react-redux";
import { useDispatch, useSelector } from "react-redux";

// ── Regular slices ────────────────────────────────────────────────────────────
import authReducer from "../../features/auth/slices/authSlice";
import navigationReducer from "../../features/navigation/slices/navigationSlice";
import shiftUiReducer from "../../features/shift-management/slice/shiftSlice";
import rolePermissionReducer from "../../features/system-administration/slice/RolePermissionSlice";
import shiftValidationUiReducer from "../../features/shift-validation/slice/shiftValidationSlice";
import commandPaletteReducer from "../../features/command-pallete/slice/Commandpaletteslice";
import businessReducer from "../../features/corporate/slice/businessSlice";

// ── Shared RTK Query instance — the only API reducer/middleware this store needs ──
import { apiBase } from "../../shared/services/apiBase";

const rootReducer = combineReducers({
  // ── Regular slices ─────────────────────────────────────────────────────────
  auth: authReducer,
  business: businessReducer,
  navigation: navigationReducer,
  commandPalette: commandPaletteReducer,
  shiftUi: shiftUiReducer,
  rolePermissionUi: rolePermissionReducer,
  shiftValidationUi: shiftValidationUiReducer,

  // ── RTK Query cache reducer (shared by every feature's injectEndpoints) ─────
  [apiBase.reducerPath]: apiBase.reducer,
});

// ─────────────────────────────────────────────────────────────────────────────
// STORE
// ─────────────────────────────────────────────────────────────────────────────

const resettableRootReducer = (
  state: ReturnType<typeof rootReducer> | undefined,
  action: { type: string },
) => {
  if (action.type === "app/resetAll") {
    return rootReducer(undefined, action as any);
  }
  return rootReducer(state, action as any);
};

export const resetAllState = () => ({ type: "app/resetAll" as const });

export const store = configureStore({
  reducer: resettableRootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ["persist/PERSIST"],
      },
    }).concat(apiBase.middleware),

  devTools: false,
});

setupListeners(store.dispatch);

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
